import { useApi } from "@/hooks";
import { BybAvatar, BybButton, BybTextField } from "@Components/common/byb-ds";
import CenteredLoadingOverlay from "@Components/generic/centered-loading-overlay";
import Icon from "@Components/generic/icon";
import { faBuilding, faSearch, faUser, faUsers } from "@fortawesome/pro-solid-svg-icons";
import useGlobalContext from "@hooks/common/general/use-global-context";
import useI18n from "@i18n";
import { Box, capitalize, FormControlLabel, Stack, Switch, Tab, Tabs, Typography, useTheme } from "@mui/material";
import { Api } from "@Shared/schemas";
import { GetAccountResponse } from "@Shared/schemas/accounts/accountSchemas";
import { TeamRecord } from "@Shared/schemas/teams/teamSchemas";
import { UserRecord } from "@Shared/schemas/users/userSchemas";
import { useEffect, useState } from "react";
import { useDebounce } from "react-use";
import { BybDialog } from "../../common";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  const theme = useTheme();

  return (
    <div role="tabpanel" hidden={value !== index} id={`sharing-tabpanel-${index}`} aria-labelledby={`sharing-tab-${index}`} {...other}>
      {value === index && <Box sx={{ padding: theme.spacing(4, 0) }}>{children}</Box>}
    </div>
  );
};

interface IPlaylistSharingDialog {
  open: boolean;
  onClose: () => void;
  onSelect: (users: UserRecord[], teams: TeamRecord[], accounts: GetAccountResponse[]) => void;
  initialUsers?: UserRecord[];
  initialTeams?: TeamRecord[];
  initialAccounts?: GetAccountResponse[];
  isSuperAdminMode?: boolean;
}

const PlaylistSharingDialog = ({
  onClose,
  onSelect,
  initialUsers = [],
  initialTeams = [],
  initialAccounts = [],
  open,
  ...props
}: IPlaylistSharingDialog) => {
  const theme = useTheme();
  const { i18n } = useI18n();
  const { globalState } = useGlobalContext();
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [throttledSearchQuery, setThrottledSearchQuery] = useState("");
  const [throttledLoading, setThrottledLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [isSuperAdminMode, setIsSuperAdminMode] = useState(false);
  const [filterSelected, setFilterSelected] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  // Selected items state
  const [selectedUsers, setSelectedUsers] = useState<UserRecord[]>(initialUsers);
  const [selectedTeams, setSelectedTeams] = useState<TeamRecord[]>(initialTeams);
  const [selectedAccounts, setSelectedAccounts] = useState<GetAccountResponse[]>(initialAccounts);

  // Update selected items when initial values change
  useEffect(() => {
    setSelectedUsers(initialUsers);
    setSelectedTeams(initialTeams);
    setSelectedAccounts(initialAccounts);
    // Reset the initialDataLoaded flag when initialUsers/Teams/Accounts change
    setInitialDataLoaded(false);
  }, [initialUsers, initialTeams, initialAccounts]);

  // Search results state with caching
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [teams, setTeams] = useState<TeamRecord[]>([]);
  const [accounts, setAccounts] = useState<GetAccountResponse[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [lastQueries, setLastQueries] = useState<{ [key: string]: string }>({
    users: "",
    teams: "",
    accounts: "",
  });
  const [initialLoads, setInitialLoads] = useState<{ [key: string]: boolean }>({
    users: false,
    teams: false,
    accounts: false,
  });

  const [, cancel] = useDebounce(
    () => {
      setThrottledSearchQuery(searchQuery);
    },
    500,
    [searchQuery]
  );

  // Enhanced API calls to fetch complete data for initial items
  const { call: fetchInitialUsers, loading: loadingInitialUsers } = useApi<typeof Api.Account.SearchUsers>({
    api: Api.Account.SearchUsers,
    request: {
      search_query: "",
      limit: 100,
      user_ids: selectedUsers.map((user) => user.id),
      include_accounts: isSuperAdminMode ? undefined : [globalState.account?.id!],
    },
    onSuccess: (response: { data: UserRecord[] }) => {
      if (response.data.length > 0) {
        // Create a map of user IDs to complete user records
        const userMap = response.data.reduce((acc, user) => {
          acc[user.id] = user;
          return acc;
        }, {} as Record<number, UserRecord>);

        // Update selected users with complete data, preserving any that weren't found
        setSelectedUsers((prev) => prev.map((user) => userMap[user.id] || user));

        // Also update the users array to include these complete records
        setUsers((prevUsers) => {
          const existingIds = new Set(prevUsers.map((u) => u.id));
          const newUsers = response.data.filter((user) => !existingIds.has(user.id));
          return [...prevUsers, ...newUsers];
        });
      }
    },
  });

  const { call: fetchInitialTeams, loading: loadingInitialTeams } = useApi<typeof Api.Teams.SearchTeams>({
    api: Api.Teams.SearchTeams,
    request: {
      search_query: "",
      limit: 100,
      // Using a custom field to pass team IDs - if API doesn't support this,
      // you'll need to modify it or load teams individually
      include_accounts: isSuperAdminMode ? undefined : [globalState.account?.id!],
    },
    onSuccess: (response: { data: TeamRecord[]; total_count: number }) => {
      if (response.data.length > 0) {
        // Filter teams to match our selected teams' IDs
        const selectedTeamIds = new Set(selectedTeams.map((team) => team.id));
        const matchingTeams = response.data.filter((team) => selectedTeamIds.has(team.id));

        // Create a map of team IDs to complete team records
        const teamMap = matchingTeams.reduce((acc, team) => {
          acc[team.id] = team;
          return acc;
        }, {} as Record<number, TeamRecord>);

        // Update selected teams with complete data, preserving any that weren't found
        setSelectedTeams((prev) => prev.map((team) => teamMap[team.id] || team));

        // Also update the teams array to include these complete records
        setTeams((prevTeams) => {
          const existingIds = new Set(prevTeams.map((t) => t.id));
          const newTeams = matchingTeams.filter((team) => !existingIds.has(team.id));
          return [...prevTeams, ...newTeams];
        });
      }
    },
  });

  const { call: fetchInitialAccounts, loading: loadingInitialAccounts } = useApi<typeof Api.Account.SearchAccounts>({
    api: Api.Account.SearchAccounts,
    request: {
      search_query: "",
      limit: 100,
    },
    onSuccess: (response: { data: GetAccountResponse[]; total_count: number }) => {
      if (response.data.length > 0) {
        // Filter accounts to match our selected accounts' IDs
        const selectedAccountIds = new Set(selectedAccounts.map((account) => account.id));
        const matchingAccounts = response.data.filter((account) => selectedAccountIds.has(account.id));

        // Create a map of account IDs to complete account records
        const accountMap = matchingAccounts.reduce((acc, account) => {
          acc[account.id] = account;
          return acc;
        }, {} as Record<number, GetAccountResponse>);

        // Update selected accounts with complete data, preserving any that weren't found
        setSelectedAccounts((prev) => prev.map((account) => accountMap[account.id] || account));

        // Also update the accounts array to include these complete records
        setAccounts((prevAccounts) => {
          const existingIds = new Set(prevAccounts.map((a) => a.id));
          const newAccounts = matchingAccounts.filter((account) => !existingIds.has(account.id));
          return [...prevAccounts, ...newAccounts];
        });
      }
    },
  });

  // Fetch complete data for initial items when the dialog opens or initial items change
  useEffect(() => {
    if (open && !initialDataLoaded) {
      // Only fetch if we have IDs to fetch
      if (selectedUsers.length > 0) {
        fetchInitialUsers();
      }

      if (selectedTeams.length > 0) {
        fetchInitialTeams();
      }

      if (selectedAccounts.length > 0 && globalState.user?.isSuperAdmin) {
        fetchInitialAccounts();
      }

      setInitialDataLoaded(true);
    }
  }, [open, initialDataLoaded, selectedUsers.length, selectedTeams.length, selectedAccounts.length, globalState.user?.isSuperAdmin]);

  const { call: searchUsers, loading: loadingUsers } = useApi<typeof Api.Account.SearchUsers>({
    api: Api.Account.SearchUsers,
    request: {
      search_query: throttledSearchQuery,
      limit: pageSize,
      skip: (page - 1) * pageSize,
      include_accounts: isSuperAdminMode ? undefined : [globalState.account?.id!],
    },
    onSuccess: (response: { data: UserRecord[] }) => {
      // Merge new results with existing selected items
      const newUsers = response.data;
      const mergedUsers = [...newUsers];

      // Add any selected users that aren't in the new results
      selectedUsers.forEach((user) => {
        if (!newUsers.some((u) => u.id === user.id)) {
          mergedUsers.push(user);
        }
      });

      setUsers(mergedUsers);
      setTotalCount(mergedUsers.length);
      setLastQueries((prev) => ({ ...prev, users: throttledSearchQuery }));
      setInitialLoads((prev) => ({ ...prev, users: true }));
    },
  });

  const { call: searchTeams, loading: loadingTeams } = useApi<typeof Api.Teams.SearchTeams>({
    api: Api.Teams.SearchTeams,
    request: {
      search_query: throttledSearchQuery,
      limit: pageSize,
      skip: (page - 1) * pageSize,
      include_accounts: isSuperAdminMode ? undefined : [globalState.account?.id!],
    },
    onSuccess: (response: { data: TeamRecord[]; total_count: number }) => {
      // Merge new results with existing selected items
      const newTeams = response.data;
      const mergedTeams = [...newTeams];

      // Add any selected teams that aren't in the new results
      selectedTeams.forEach((team) => {
        if (!newTeams.some((t) => t.id === team.id)) {
          mergedTeams.push(team);
        }
      });

      setTeams(mergedTeams);
      setTotalCount(response.total_count);
      setLastQueries((prev) => ({ ...prev, teams: throttledSearchQuery }));
      setInitialLoads((prev) => ({ ...prev, teams: true }));
    },
  });

  const { call: searchAccounts, loading: loadingAccounts } = useApi<typeof Api.Account.SearchAccounts>({
    api: Api.Account.SearchAccounts,
    request: {
      search_query: throttledSearchQuery,
      limit: pageSize,
      skip: (page - 1) * pageSize,
    },
    onSuccess: (response: { data: GetAccountResponse[]; total_count: number }) => {
      // Merge new results with existing selected items
      const newAccounts = response.data;
      const mergedAccounts = [...newAccounts];

      // Add any selected accounts that aren't in the new results
      selectedAccounts.forEach((account) => {
        if (!newAccounts.some((a) => a.id === account.id)) {
          mergedAccounts.push(account);
        }
      });

      setAccounts(mergedAccounts);
      setTotalCount(response.total_count);
      setLastQueries((prev) => ({ ...prev, accounts: throttledSearchQuery }));
      setInitialLoads((prev) => ({ ...prev, accounts: true }));
    },
  });

  useEffect(() => {
    setPage(1);
  }, [throttledSearchQuery, tabValue]);

  useEffect(() => {
    // Only make API call if the search query has changed for the current tab or it's the first load
    const shouldSearch = () => {
      switch (tabValue) {
        case 0:
          return !initialLoads.users || lastQueries.users !== throttledSearchQuery;
        case 1:
          return !initialLoads.teams || lastQueries.teams !== throttledSearchQuery;
        case 2:
          return !initialLoads.accounts || lastQueries.accounts !== throttledSearchQuery;
        default:
          return false;
      }
    };

    if (shouldSearch()) {
      switch (tabValue) {
        case 0:
          searchUsers();
          break;
        case 1:
          searchTeams();
          break;
        case 2:
          if (globalState.user?.isSuperAdmin) {
            searchAccounts();
          }
          break;
      }
    }
  }, [throttledSearchQuery, page, pageSize, tabValue, isSuperAdminMode]);

  // Add new effect to handle super admin mode changes
  useEffect(() => {
    // Reset the last queries to force a refresh
    setLastQueries({
      users: "",
      teams: "",
      accounts: "",
    });
    setInitialLoads({
      users: false,
      teams: false,
      accounts: false,
    });
    setPage(1);
    setInitialDataLoaded(false); // Reset initial data loaded flag

    // Trigger immediate search for current tab
    switch (tabValue) {
      case 0:
        searchUsers();
        break;
      case 1:
        searchTeams();
        break;
      case 2:
        if (globalState.user?.isSuperAdmin) {
          searchAccounts();
        }
        break;
    }

    // Re-fetch initial data with the new mode
    if (selectedUsers.length > 0) {
      fetchInitialUsers();
    }

    if (selectedTeams.length > 0) {
      fetchInitialTeams();
    }

    if (selectedAccounts.length > 0 && globalState.user?.isSuperAdmin) {
      fetchInitialAccounts();
    }
  }, [isSuperAdminMode]);

  const [, cancelLoading] = useDebounce(
    () => {
      setThrottledLoading(loadingUsers || loadingTeams || loadingAccounts || loadingInitialUsers || loadingInitialTeams || loadingInitialAccounts);
    },
    500,
    [loadingUsers, loadingTeams, loadingAccounts, loadingInitialUsers, loadingInitialTeams, loadingInitialAccounts]
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSelect = (type: "user" | "team" | "account", item: UserRecord | TeamRecord | GetAccountResponse) => {
    switch (type) {
      case "user":
        setSelectedUsers((prev) => {
          const exists = prev.some((u) => u.id === (item as UserRecord).id);
          return exists ? prev.filter((u) => u.id !== (item as UserRecord).id) : [...prev, item as UserRecord];
        });
        break;
      case "team":
        setSelectedTeams((prev) => {
          const exists = prev.some((t) => t.id === (item as TeamRecord).id);
          return exists ? prev.filter((t) => t.id !== (item as TeamRecord).id) : [...prev, item as TeamRecord];
        });
        break;
      case "account":
        setSelectedAccounts((prev) => {
          const exists = prev.some((a) => a.id === (item as GetAccountResponse).id);
          return exists ? prev.filter((a) => a.id !== (item as GetAccountResponse).id) : [...prev, item as GetAccountResponse];
        });
        break;
    }
  };

  const renderItem = (type: "user" | "team" | "account", item: UserRecord | TeamRecord | GetAccountResponse) => {
    const isSelected =
      type === "user"
        ? selectedUsers.some((u) => u.id === item.id)
        : type === "team"
        ? selectedTeams.some((t) => t.id === item.id)
        : selectedAccounts.some((a) => a.id === item.id);

    const getDisplayName = () => {
      if (type === "user") {
        const user = item as UserRecord;
        // Handle cases where the user object doesn't have first_name or last_name
        if (!user.first_name || !user.last_name) {
          return (
            user.email || i18n("playlists.playlist.sharing.user.name", { name: `User ${user.id}` }) || i18n("playlists.playlist.sharing.user.unknown")
          );
        }
        return `${capitalize(user.first_name)} ${capitalize(user.last_name)}`;
      }
      if (type === "team") {
        const team = item as TeamRecord;
        return team.name ? capitalize(team.name) : i18n("playlists.playlist.sharing.no.name");
      }
      if (type === "account") {
        const account = item as GetAccountResponse;
        return account.name ? capitalize(account.name) : i18n("playlists.playlist.sharing.no.name");
      }
      return i18n("playlists.playlist.sharing.unknown");
    };

    const getSubtitle = () => {
      if (type === "user") {
        const user = item as UserRecord;
        // If email is missing but we have name info, show that instead
        if (!user.email && (user.first_name || user.last_name)) {
          return `${user.first_name || ""} ${user.last_name || ""}`.trim();
        }
        return user.email || `ID: ${user.id}`;
      }
      if (type === "team") {
        const team = item as TeamRecord;
        return team.group
          ? i18n("playlists.playlist.sharing.group.name", { name: capitalize(team.group) })
          : i18n("playlists.playlist.sharing.no.group");
      }
      if (type === "account") {
        const account = item as GetAccountResponse;
        return i18n("playlists.playlist.sharing.members", { count: account.members?.length || 0 });
      }
      return "";
    };

    const getIcon = () => {
      switch (type) {
        case "user":
          return faUser;
        case "team":
          return faUsers;
        case "account":
          return faBuilding;
      }
    };
    return (
      <Stack
        key={item.id}
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{
          padding: theme.spacing(2),
          borderRadius: theme.spacing(1),
          backgroundColor: isSelected ? theme.palette.dsPrimary.v200 : "transparent",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: isSelected ? theme.palette.dsPrimary.v200 : theme.palette.action.hover,
          },
        }}
        onClick={() => handleSelect(type, item)}
      >
        {type !== "user" ? (
          <Icon icon={getIcon()} size="2xl" style={{ marginRight: theme.spacing(1) }} />
        ) : (
          <BybAvatar size={6} src={(item as UserRecord)?.avatar || undefined} alt={getDisplayName()} user={item as UserRecord} />
        )}
        <Stack spacing={0}>
          <Typography variant="textBase600" color="dsBase.v200">
            {getDisplayName()}
          </Typography>
          <Typography variant="textBase500" color="dsNeutral.v200">
            {getSubtitle()}
          </Typography>
        </Stack>
      </Stack>
    );
  };

  const filterItems = (items: any[], selectedItems: any[], searchText: string) => {
    let filtered = items;

    // First filter by selected state if filterSelected is true
    if (filterSelected) {
      filtered = items.filter((item) => selectedItems.some((selected) => selected.id === item.id));
    }

    // Then filter by search text if provided
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((item) => {
        if ("first_name" in item) {
          // User - safely check each property exists
          const nameMatches = item.first_name && item.last_name ? `${item.first_name} ${item.last_name}`.toLowerCase().includes(searchLower) : false;
          const emailMatches = item.email ? item.email.toLowerCase().includes(searchLower) : false;
          const idMatches = item.id ? String(item.id).includes(searchLower) : false;

          return nameMatches || emailMatches || idMatches;
        } else if ("group" in item) {
          // Team
          const nameMatches = item.name ? item.name.toLowerCase().includes(searchLower) : false;
          const groupMatches = item.group ? item.group.toLowerCase().includes(searchLower) : false;

          return nameMatches || groupMatches;
        } else {
          // Account
          return item.name ? item.name.toLowerCase().includes(searchLower) : false;
        }
      });
    }

    return filtered;
  };

  const getFilteredItems = () => {
    switch (tabValue) {
      case 0:
        return filterItems(users, selectedUsers, searchQuery).map((item) => ({ type: "user" as const, item }));
      case 1:
        return filterItems(teams, selectedTeams, searchQuery).map((item) => ({ type: "team" as const, item }));
      case 2:
        return filterItems(accounts, selectedAccounts, searchQuery).map((item) => ({ type: "account" as const, item }));
      default:
        return [];
    }
  };

  return (
    <BybDialog
      title={i18n("playlists.playlist.sharing.share.title")}
      name="playlist-sharing-dialog"
      open={open}
      onClose={() => onClose()}
      {...props}
      sx={{
        "& .MuiDialog-paper": {
          minHeight: "70vh",
          minWidth: theme.spacing(76),
          overflow: "auto",
          [theme.breakpoints.down("lg")]: {
            minWidth: theme.spacing(60),
          },
        },
      }}
      dialogActions={
        <>
          <BybButton onClick={onClose} color="secondary" fullWidth>
            {i18n("cancel")}
          </BybButton>
          <BybButton
            onClick={() => {
              onSelect(selectedUsers, selectedTeams, selectedAccounts);
              onClose();
            }}
            color="primary"
            fullWidth
          >
            {i18n("share")}
          </BybButton>
        </>
      }
    >
      {globalState.user?.isSuperAdmin && (
        <FormControlLabel
          sx={{ position: "absolute", top: theme.spacing(3.5), right: theme.spacing(2) }}
          label={i18n("settings.page.link.super-admin")}
          control={
            <Switch
              checked={isSuperAdminMode}
              onChange={(e) => {
                setIsSuperAdminMode(e.target.checked);
                setPage(1); // Reset page when switching modes
              }}
            />
          }
        />
      )}
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="sharing tabs">
            <Tab label={i18n("users")} />
            <Tab label={i18n("teams")} />
            {isSuperAdminMode && <Tab label={i18n("accounts")} />}
          </Tabs>
          <FormControlLabel
            control={
              <Switch
                checked={filterSelected}
                onChange={(e) => {
                  setFilterSelected(e.target.checked);
                  setPage(1);
                }}
              />
            }
            label={i18n("playlists.playlist.sharing.filter-selected")}
          />
        </Stack>
        <Stack spacing={0}>
          <BybTextField
            disabled={throttledLoading || filterSelected}
            variant="outlined"
            size="small"
            sx={{
              "& .MuiInputBase-root": {
                padding: theme.spacing(0.5),
              },
              "& .MuiInputBase-input": {
                paddingTop: theme.spacing(1),
                paddingBottom: theme.spacing(1),
              },
            }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={`Search ${tabValue === 0 ? "users" : tabValue === 1 ? "teams" : "accounts"}`}
            InputProps={{
              startAdornment: <Icon icon={faSearch} style={{ marginRight: theme.spacing(1), marginLeft: theme.spacing(1) }} />,
            }}
            fullWidth
          />

          {throttledLoading ? (
            <CenteredLoadingOverlay loading={throttledLoading} />
          ) : (
            <Box
              sx={{
                height: `calc(100vh - ${totalCount > pageSize ? "810px" : "770px"})`,
                minHeight: theme.spacing(40),
                overflow: "auto",
              }}
            >
              <CustomTabPanel value={tabValue} index={0}>
                <Stack spacing={0.5}>{getFilteredItems().map(({ type, item }) => renderItem(type, item))}</Stack>
              </CustomTabPanel>

              <CustomTabPanel value={tabValue} index={1}>
                <Stack spacing={0.5}>{getFilteredItems().map(({ type, item }) => renderItem(type, item))}</Stack>
              </CustomTabPanel>

              {globalState.user?.isSuperAdmin && (
                <CustomTabPanel value={tabValue} index={2}>
                  <Stack spacing={0.5}>{getFilteredItems().map(({ type, item }) => renderItem(type, item))}</Stack>
                </CustomTabPanel>
              )}
            </Box>
          )}

          {!throttledLoading && totalCount > pageSize && (
            <Stack direction="row" alignItems="center" spacing={theme.spacing(2)} justifyContent="right">
              <BybButton
                size="small"
                color="primary"
                sx={{ width: "100px" }}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={page === 1}
              >
                {i18n("playlists.playlist.sharing.previous")}
              </BybButton>
              <Typography variant="textBase600" sx={{ color: theme.palette.dsBase.v200 }}>
                {i18n("playlists.playlist.sharing.page", { page: page, total: Math.ceil(totalCount / pageSize) })}
              </Typography>
              <BybButton
                size="small"
                color="primary"
                sx={{ width: "100px" }}
                onClick={() => setPage((prev) => prev + 1)}
                disabled={page * pageSize >= totalCount}
              >
                {i18n("playlists.playlist.sharing.next")}
              </BybButton>
            </Stack>
          )}
        </Stack>
      </Stack>
    </BybDialog>
  );
};

export default PlaylistSharingDialog;
